import { useEffect } from 'react';

/**
 * Session 215.80 — Lightweight per-route SEO hook.
 *
 * React SPAs share a single index.html at request time; Googlebot
 * renders JS but many other crawlers (Bing, LinkedIn, Twitter,
 * Slack unfurler, iMessage preview) parse the initial HTML for
 * title / description / canonical / OG tags. Setting them client-
 * side after mount also updates the browser tab and shows up in
 * GSC "Rendered HTML".
 *
 * Usage inside any public page:
 *
 *   useSEO({
 *     title: 'Get a Trucking Insurance Quote | Zion Enterprises',
 *     description: 'Compare APD, MTC, TI, NTL and TGL rates…',
 *     path: '/get-a-quote',
 *   });
 *
 * Only the "public" marketing pages need this — authenticated
 * screens are excluded from indexing by robots.txt anyway.
 */
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://zionuw.com';

function setOrCreateMeta(attr, key, content) {
  if (content == null) return;
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(id, data) {
  const scriptId = `ld-json-${id}`;
  let el = document.head.querySelector(`script#${scriptId}`);
  if (!el) {
    el = document.createElement('script');
    el.id = scriptId;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function useSEO({
  title,
  description,
  path = '',
  image,
  noindex = false,
  jsonLd = null,      // Session 215.82 — optional page-specific structured data
  jsonLdId = 'page',
} = {}) {
  useEffect(() => {
    const canonical = `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
    const ogImage = image || `${SITE_URL}/zion_enterprises_logo.png`;

    if (title) document.title = title;
    if (description) setOrCreateMeta('name', 'description', description);
    setOrCreateMeta(
      'name',
      'robots',
      noindex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    );

    // Open Graph
    if (title) setOrCreateMeta('property', 'og:title', title);
    if (description) setOrCreateMeta('property', 'og:description', description);
    setOrCreateMeta('property', 'og:url', canonical);
    setOrCreateMeta('property', 'og:image', ogImage);
    setOrCreateMeta('property', 'og:type', 'website');
    setOrCreateMeta('property', 'og:site_name', 'Zion Enterprises Platform');

    // Twitter
    setOrCreateMeta('name', 'twitter:card', 'summary_large_image');
    if (title) setOrCreateMeta('name', 'twitter:title', title);
    if (description) setOrCreateMeta('name', 'twitter:description', description);
    setOrCreateMeta('name', 'twitter:image', ogImage);

    setCanonical(canonical);

    if (jsonLd) setJsonLd(jsonLdId, jsonLd);
  }, [title, description, path, image, noindex, jsonLd, jsonLdId]);
}
